import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Button,
  TablePagination,
  Typography,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ReusableHeadingComponents from "../../components/reusableComponents/reusableHeadingComponents";
import ReusableButton from "../../components/reusableComponents/reusableButtonComponents";
import ApiService from "../../_services/ApiService";
import { CardSkeletonCollab } from "../../components/reusableComponents/SkeletonCard";
import NoResults from "../../components/reusableComponents/NoResults";
import { useHistory, useLocation } from "react-router-dom";
import bgimage from "../../img/no_image.png";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import Tablecomponent from "../../components/reusableComponents/Tablecomponent";
import { useMediaQuery, useTheme } from "@mui/material";
import GigCard from "./GigCard";

const GigManagement = () => {
  const history = useHistory();
  const location = useLocation();
  const [gigList, setGigList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [tabValue, setTabValue] = useState(0);

  // 🔹 NEW: gig types from API + selected type id
  const [gigTypes, setGigTypes] = useState([{ id: null, TypeName: "All" }]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);

  const userData = useSelector((state) => state.userData.userData);
  const isDraft = location.pathname.includes("view-gig-draft");
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const stripHtml = (html) =>
    typeof html === "string" ? html.replace(/<[^>]+>/g, "") : "";

  const columns = [
    {
      headerName: "Brand",
      field: "BrandLogoURL",
      flex: 0.8,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center">
          <img
            src={row?.BrandLogoURL || bgimage}
            alt="brand-logo"
            width={55}
            height={55}
            style={{
              borderRadius: "8px",
              objectFit: "contain",
              backgroundColor: "#F5F5F5",
            }}
          />
        </Box>
      ),
    },
    {
      headerName: "Gig Title",
      field: "Title",
      flex: 2,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
            {row?.Title || "--"}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#7A7A7A" }}>
            {row?.BrandName || "--"}
          </Typography>
        </Box>
      ),
    },
    {
      headerName: "Description",
      field: "description",
      flex: 1.2,
      renderCell: ({ row }) => {
        const fullText =
          row?.SubTitle ||
          stripHtml(row?.AboutGigWork) ||
          "";

        return (
          <Tooltip
            title={
              <Typography
                sx={{
                  fontSize: "13px",
                  whiteSpace: "pre-line",
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {fullText}
              </Typography>
            }
            placement="bottom"
            arrow
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#757575",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                cursor: "pointer",
                maxWidth: 300,
              }}
            >
              {fullText}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      headerName: "Deadline",
      field: "DeadLineDate",
      flex: 1,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "14px", color: "#7A7A7A" }}>
          {row?.DeadLineDate || "--"}
        </Typography>
      ),
    },
    {
      headerName: "Rewards",
      field: "RewardAmount",
      flex: 1,
      renderCell: ({ row }) => (
        <Typography sx={{ fontWeight: 600, color: "#219653" }}>
          {row?.RewardAmount
            ? `₹${Number(row.RewardAmount).toLocaleString("en-IN")}`
            : "--"}
        </Typography>
      ),
    },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1.2,
      renderCell: ({ row }) => (
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              textTransform: "none",
              borderColor: "#ED1C24",
              color: "#ED1C24",
            }}
            onClick={() =>
              history.push(`/gig-management/edit-gig/${row?.id}`, {
                eventData: row,
              })
            }
          >
            Edit
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              backgroundColor: "#ED1C24",
              "&:hover": { backgroundColor: "#c3080d" },
            }}
            onClick={() =>
              history.push(`/gig-management/view-gig/${row.id}/details`, {
                eventData: row,
              })
            }
          >
            View
          </Button>
        </Box>
      ),
    },
  ];

  // 🔹 NEW: fetch gig types for tabs
  useEffect(() => {
    ApiService.get("gigsType")
      .then((res) => {
        const apiData = res?.data?.data || res?.data || [];
        const published = apiData.filter((t) => t.Published);
        // first tab is "All"
        setGigTypes([{ id: null, TypeName: "All" }, ...published]);
      })
      .catch((err) => {
        console.error("Error fetching gig types", err);
      });
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0); // reset pagination when searching
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);


  // -------- FETCH GIGS ----------
  const fetchData = () => {
    setLoader(true);

    const params = {
      offset: page,
      limit: rowsPerPage,
      is_draft: isDraft,
      brand_id: userData?.brand,
    };

    if (selectedTypeId) {
      params.Type = selectedTypeId;
    }

    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

    ApiService.get("gigs", params)
      .then((res) => {
        if (res?.status === 200 || res?.status === 201) {
          setCount(res?.data?.count);
          setGigList(res?.data?.data);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (userData) fetchData();
  }, [isDraft, userData, page, rowsPerPage, selectedTypeId, debouncedSearch]); 

  const filteredData = gigList.filter((g) =>
    g?.Title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewDrafts = () => {
    history.push("/gig-management/view-gig-draft");
  };

  // 🔹 When tab changes, update tab index + selectedTypeId + reset page
  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    const selectedType = gigTypes[newValue];
    setSelectedTypeId(selectedType?.id || null); // null for "All"
    setPage(0);
  };

  return (
    <Box p={4}>
      {/* HEADER */}
      <Box
        display="flex"
        alignItems={{ xs: "none", sm: "center" }}
        justifyContent="space-between"
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          paddingBottom: { xs: "20px", sm: 0 },
        }}
      >
        <ReusableHeadingComponents
          heading="Gig Posting and Management"
          subHeading="Easily post, manage, and track all your gigs in one place."
        />

        <Box display="flex" gap={2}>
          <ReusableButton
            children="View Drafts"
            variant="outlined"
            classNames="gn-actionbtn-outline"
            onClick={handleViewDrafts}
          />

          <ReusableButton
            children="Create Gig Work"
            variant="contained"
            classNames="gn-actionbtn"
            onClick={() =>
              history.push("/gig-management/create-gig-management")
            }
          />
        </Box>
      </Box>

      {/* Search + Toggle */}
      <Box
        mt={1}
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <TextField
          placeholder="Search Gig"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: { xs: "100%", sm: "500px" }, background: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#7A7A7A" }} />
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" alignItems="center">
          <Tooltip title="Grid View">
            <IconButton
              onClick={() => setViewMode("grid")}
              sx={{
                border: "1px solid #7A7A7A",
                borderRight: "none",
                borderRadius: "8px 0 0 8px",
                width: 40,
                height: 40,
                backgroundColor:
                  viewMode === "grid" ? "#7A7A7A" : "transparent",
                color: viewMode === "grid" ? "#FFFFFF" : "#7A7A7A",
              }}
            >
              <GridViewIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Table View">
            <IconButton
              onClick={() => setViewMode("table")}
              sx={{
                border: "1px solid #7A7A7A",
                borderRadius: "0 8px 8px 0",
                width: 40,
                height: 40,
                backgroundColor:
                  viewMode === "table" ? "#7A7A7A" : "transparent",
                color: viewMode === "table" ? "#FFFFFF" : "#7A7A7A",
              }}
            >
              <ViewListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* TABS – now from gigsType */}
      <Box
        sx={{
          backgroundColor: "white",
          mb: 2,
          pt: "8px",
          borderRadius: "8px",
          pl: "16px",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          // variant={isMobileOrTablet ? "scrollable" : "standard"}
          variant="scrollable"
          // scrollButtons="auto"
          scrollButtons={isMobileOrTablet ? "auto" : false}
          allowScrollButtonsMobile={isMobileOrTablet}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              color: "#6B6B6B",
              minHeight: "36px !important",
              padding: "0 12px !important",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px",
            },
            "& .MuiTab-wrapper": {
              padding: 0,
              margin: 0,
              lineHeight: "36px",
              display: "flex",
              alignItems: "center",
            },
            "& .Mui-selected": {
              color: "#fff !important",
              backgroundColor: "#F14950",
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          {gigTypes.map((tab, i) => (
            <Tab key={tab.id ?? "all"} label={tab.TypeName} />
          ))}
        </Tabs>
      </Box>

      {/* ------------ GRID OR TABLE VIEW ------------ */}
      {viewMode === "grid" ? (
        <Grid container spacing={2}>
          {loader
            ? Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <CardSkeletonCollab />
              </Grid>
            ))
            : filteredData.length
              ? filteredData.map((data, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <GigCard
                    gig={data}
                    onView={() =>
                      history.push(`/gig-management/view-gig/${data.id}/details`, {
                        eventData: data,
                      })
                    }
                    onEdit={() =>
                      history.push(`/gig-management/edit-gig/${data?.id}`, {
                        eventData: data,
                      })
                    }
                  />
                </Grid>
              ))
              : (
                <NoResults eventNoData="No Gig Found" />
              )}
        </Grid>
      ) : (
        <Tablecomponent
          loader={loader}
          columns={columns}
          data={filteredData}
          rowsPerPage={rowsPerPage}
          page={page}
          count={count}
          onPageChange={setPage}
          rowsPerPageOptions={[5, 10, 20]}
          onRowsPerPageChange={(e) =>
            setRowsPerPage(parseInt(e.target.value, 10))
          }
        />
      )}

      {/* Pagination */}
      {viewMode === "grid" && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) =>
            setRowsPerPage(parseInt(e.target.value, 10))
          }
          sx={{
            "& .MuiTablePagination-selectLabel": {
              paddingTop: "14px",
            },
            "& .MuiTablePagination-select": {
              display: "inline-flex",
              alignItems: "center",
              paddingY: "0px",
            },
            "& .MuiTablePagination-displayedRows": {
              margin: 0,
            },
          }}
        />
      )}
    </Box>
  );
};

export default GigManagement;
